import { useMemo, useState } from 'react';
import {
  Table,
  ActionIcon,
  Text,
  Menu,
  rem,
  TextInput,
  Paper,
  Stack,
  Badge,
  ThemeIcon,
  Group,
} from '@mantine/core';
import { IconDotsVertical, IconEdit, IconTrash, IconSearch } from '@tabler/icons-react';
import type { Category } from '@/types/models';

interface CategoriesTableProps {
  categories: Category[];
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  isLoading?: boolean;
}

export function CategoriesTable({ categories, onEdit, onDelete, isLoading }: CategoriesTableProps) {
  const [search, setSearch] = useState('');

  const filteredCategories = useMemo(() => {
    return categories.filter((category) => {
      const matchesSearch =
        search === '' || category.name.toLowerCase().includes(search.toLowerCase());
      return matchesSearch;
    });
  }, [categories, search]);

  const rows = filteredCategories.map((category) => (
    <Table.Tr key={category.id}>
      <Table.Td>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {category.icon && (
            <ThemeIcon variant="light" size="lg">
              <span style={{ fontSize: '1.2rem' }}>{category.icon}</span>
            </ThemeIcon>
          )}
          <Text fw={500}>{category.name}</Text>
        </div>
      </Table.Td>
      <Table.Td>
        {category.status ? (
          <Badge variant="light" color={category.status === 'active' ? 'green' : 'gray'}>
            {category.status}
          </Badge>
        ) : (
          '-'
        )}
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => onEdit(category)}
            title="Edit"
          >
            <IconEdit size={16} />
          </ActionIcon>
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                color="red"
                leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                onClick={() => onDelete(category)}
              >
                Delete
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <TextInput
          placeholder="Search categories..."
          leftSection={<IconSearch size={16} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          style={{ maxWidth: 400 }}
        />
      </Paper>

      <Paper withBorder>
        <Table.ScrollContainer minWidth={500}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading ? (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text ta="center" py="xl" c="dimmed">
                      Loading categories...
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3}>
                    <Text ta="center" py="xl" c="dimmed">
                      No categories found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>

      <Text size="sm" c="dimmed">
        Showing {filteredCategories.length} of {categories.length} categories
      </Text>
    </Stack>
  );
}
